import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Card } from "react-bootstrap";
import { find, get, isEmpty, map } from "lodash";
import { toast } from "react-toastify";

import {
  getUser,
  getUserAlloyApplication,
  updateUserKyc,
} from "actions/adminActions";
import { adminConstants } from "actions/types";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { getBaseAlloyUrl } from "utils/alloyHelpers";
import type { RootState } from "types/state";
import type { UpdateUserKycInput } from "types/graphql";

import IconSpinner from "components/IconSpinner";
import IconTable from "components/IconTable";
import type { IconTableColumn } from "components/IconTable";
import IconTableHeader from "components/IconTableHeader";
import Button from "components/Button";
import Alert from "components/Alert";
// MemberFieldEditModal is TS with precise props (memberId: string, fieldType:
// FieldType). This page passes the optional userId (string | undefined) and
// editFieldType (FieldType | null), so cast to the looser local prop shape so
// those values pass through unchanged.
import MemberFieldEditModalComponent from "../employers/MemberFieldEditModal";

type FieldType = "ssn" | "dob" | "address";

interface MemberFieldEditModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (data: FieldEditSubmitData) => void | Promise<void>;
  memberName: string;
  memberId?: string;
  fieldType: FieldType | null;
  client?: ApolloClient<object>;
  currentSsn?: string;
  currentDob?: string;
  currentAddress?: {
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  };
  isSubmitting?: boolean;
}

const MemberFieldEditModal =
  MemberFieldEditModalComponent as React.ComponentType<MemberFieldEditModalProps>;

/** Payload handed to onSubmit by MemberFieldEditModal. */
interface FieldEditSubmitData {
  memberId?: string;
  fieldType: FieldType;
  value?: string;
  address?: UpdateUserKycInput["address"];
}

/** Single status row rendered by the Alloy KYC IconTable. */
interface StatusRow {
  entityId: string;
  entityName: string;
  entityType: string;
  status: string;
  tags: string[];
}

interface OwnProps {
  userId?: string;
}

const errorSelector = createErrorSelector([
  adminConstants.GET_ALLOY_APPLICATION,
  adminConstants.GET_USER,
]);

const updateKycErrorSelector = createErrorSelector([
  adminConstants.UPDATE_USER_KYC,
]);

const isFetchingSelector = createLoadingSelector([
  adminConstants.GET_ALLOY_APPLICATION,
  adminConstants.GET_USER,
]);

const mapStateToProps = (state: RootState) => {
  return {
    error: errorSelector(state),
    updateKycError: updateKycErrorSelector(state),
    isFetching: isFetchingSelector(state),
  };
};

const mapDispatchToProps = {
  getUser,
  getUserAlloyApplication,
  updateUserKyc,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  initialFetching: boolean;
  alloyApplication: Record<string, unknown>;
  userDetails: Record<string, unknown>;
  showEditModal: boolean;
  editFieldType: FieldType | null;
  isSubmittingEdit: boolean;
}

class UserYellowPath extends React.PureComponent<Props, State> {
  state: State = {
    initialFetching: true,
    alloyApplication: {},
    userDetails: {},
    showEditModal: false,
    editFieldType: null,
    isSubmittingEdit: false,
  };

  async componentDidMount() {
    await this._loadData();
    this.setState({ initialFetching: false });
  }

  _loadData = async () => {
    const [alloyResult, userResult] = await Promise.all([
      this.props.getUserAlloyApplication(
        this.props.client as ApolloClient<object>,
        this.props.userId as string
      ),
      this.props.getUser(
        this.props.client as ApolloClient<object>,
        this.props.userId as string
      ),
    ]);

    this.setState({
      alloyApplication: (get(alloyResult, "data", {}) || {}) as Record<
        string,
        unknown
      >,
      userDetails: (get(userResult, "data", {}) || {}) as Record<
        string,
        unknown
      >,
    });
  };

  _getPersonEntity = (): Record<string, unknown> => {
    const childEntities = get(
      this.state.alloyApplication,
      "embedded.childEntities",
      []
    ) as Array<Record<string, unknown>>;
    return find(childEntities, { type: "person" }) || {};
  };

  _getEntityApplication = (): Record<string, unknown> => {
    const person = this._getPersonEntity();
    const entityApplications = get(
      this.state.alloyApplication,
      "embedded.entityApplications",
      []
    ) as Array<Record<string, unknown>>;
    if (!person.token) return {};
    return find(entityApplications, { entityToken: person.token }) || {};
  };

  _getAllTags = (): string[] => {
    return get(this._getEntityApplication(), "output.tags", []) as string[];
  };

  _getEditableFields = (allTags: string[]): FieldType[] => {
    const fields: FieldType[] = [];
    if (allTags.includes("SSN Miskey")) fields.push("ssn");
    if (allTags.includes("DOB Miskey")) fields.push("dob");
    if (
      allTags.includes("Address Warning") ||
      allTags.includes("CIP Address Not Verified")
    ) {
      fields.push("address");
    }
    return fields;
  };

  _openEditModal = (fieldType: FieldType) => {
    this.setState({ showEditModal: true, editFieldType: fieldType });
  };

  _closeEditModal = () => {
    this.setState({ showEditModal: false, editFieldType: null });
  };

  _handleFieldEditSubmit = async (data: FieldEditSubmitData) => {
    this.setState({ isSubmittingEdit: true });

    const params: UpdateUserKycInput = {
      // userId is optional OwnProps but UpdateUserKycInput requires it;
      // preserve the original behavior of passing it through verbatim
      userId: this.props.userId as string,
      fieldType: data.fieldType,
    };

    if (data.fieldType === "address") {
      params.address = data.address;
    } else {
      params.value = data.value;
    }

    const result = await this.props.updateUserKyc(
      this.props.client as ApolloClient<object>,
      params
    );

    this.setState({ isSubmittingEdit: false });

    if (result.success) {
      toast.success("Successfully submitted for approval.");
      this._closeEditModal();
      await this._loadData();
    } else {
      toast.error("Something went wrong submitting the edit request.");
    }
  };

  _getStatusData = (): StatusRow[] => {
    const person = this._getPersonEntity();
    const entityApplication = this._getEntityApplication();

    if (isEmpty(person) && isEmpty(entityApplication)) {
      return [];
    }

    return [
      {
        entityId: (person.externalEntityIdentifier as string) || "",
        entityName: (person.name as string) || "",
        entityType: (person.type as string) || "",
        status: (entityApplication.entityApplicationStatus as string) || "",
        tags: this._getAllTags(),
      },
    ];
  };

  _buildStatusColumns = (): Array<IconTableColumn<StatusRow>> => {
    return [
      { label: "Entity ID", key: "entityId" },
      { label: "Entity Name", key: "entityName" },
      { label: "Entity Type", key: "entityType" },
      { label: "Status", key: "status" },
      {
        label: "Tags",
        customComponent: ({ tags }: StatusRow) => {
          if (isEmpty(tags)) {
            return <div>—</div>;
          }
          return (
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {map(tags, (tag, idx) => (
                <li key={idx}>{tag}</li>
              ))}
            </ul>
          );
        },
      },
      {
        label: "Actions",
        customComponent: ({ tags }: StatusRow) => {
          const editableFields = this._getEditableFields(tags || []);
          if (!editableFields.length) {
            return <div>—</div>;
          }
          const fieldLabels: Record<FieldType, string> = {
            ssn: "SSN",
            dob: "DOB",
            address: "Address",
          };
          return (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {editableFields.map((field) => (
                /* @ts-expect-error preserve omitted required Button name prop */
                <Button
                  key={field}
                  btnLabel={`Edit ${fieldLabels[field]}`}
                  size="sm"
                  color="action"
                  onClick={() => this._openEditModal(field)}
                />
              ))}
            </div>
          );
        },
      },
    ];
  };

  _getUserName = (): string => {
    const person = this._getPersonEntity();
    if (person.name) return person.name as string;
    const firstName = get(this.state.userDetails, "user.firstName", "");
    const lastName = get(this.state.userDetails, "user.lastName", "");
    return `${String(firstName)} ${String(lastName)}`.trim();
  };

  _getCurrentAddress = () => {
    const userProfile = get(
      this.state.userDetails,
      "userProfile",
      {}
    ) as Record<string, unknown>;
    return {
      address1: (userProfile.address1 as string) || "",
      address2: (userProfile.address2 as string) || "",
      city: (userProfile.city as string) || "",
      state: (userProfile.state as string) || "",
      postalCode: (userProfile.postalCode as string) || "",
    };
  };

  render() {
    if (this.state.initialFetching) {
      return <IconSpinner centered />;
    }

    if (this.props.error) {
      return <Alert type="error" msg={this.props.error} />;
    }

    const data = this._getStatusData();
    const columns = this._buildStatusColumns();
    const selfHref = get(this.state.alloyApplication, "links.self.href") as
      | string
      | undefined;
    const dashboardPath = selfHref
      ? selfHref.replace(/^\/v1\/journeys\//, "/v3/dashboard/journeys/")
      : null;
    const alloyUrl = dashboardPath
      ? `${getBaseAlloyUrl()}${dashboardPath}`
      : null;

    return (
      <Card.Body className="member-docs">
        {this.state.showEditModal && (
          <MemberFieldEditModal
            show={this.state.showEditModal}
            onClose={this._closeEditModal}
            onSubmit={this._handleFieldEditSubmit}
            memberName={this._getUserName()}
            memberId={this.props.userId}
            fieldType={this.state.editFieldType}
            client={this.props.client}
            currentSsn={
              get(this.state.userDetails, "userProfile.ssn") as
                | string
                | undefined
            }
            currentDob={
              get(this.state.userDetails, "userProfile.dob") as
                | string
                | undefined
            }
            currentAddress={this._getCurrentAddress()}
            isSubmitting={this.state.isSubmittingEdit}
          />
        )}

        <h3>KYC Details:</h3>
        {alloyUrl && (
          <div style={{ marginBottom: 12 }}>
            <a
              className="icon-link"
              href={alloyUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              View in Alloy
            </a>
          </div>
        )}
        <IconTableHeader tableHeader="Status" />
        <IconTable columns={columns} data={data} />
      </Card.Body>
    );
  }
}

export default connector(withApollo<Props>(UserYellowPath));
